import { Input, Skeleton, Card, Empty } from 'antd';
import { SearchOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useGetPatientList } from 'src/hooks/patient';
import { useNavigate } from 'react-router-dom';
import { PatientDetailsBar } from '../details/detailsBar';
import { Patient } from 'src/models';
import { useAuth } from '@frontegg/react';
import { getRecentlyViewedPatients } from 'src/utils';
import { usePageHeader } from 'src/hooks/misc';

export const PatientSearch = () => {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetPatientList({ search });

  const { user } = useAuth();

  const onPatientSelect = (patientId: string | number) => {
    navigate(`/patients/${patientId}`);
  };

  usePageHeader({
    title: 'Patient Search',
  });

  useEffect(() => {
    // @ts-expect-error
    analytics.track('Landed on Patient Search', {
      email: user?.email,
    });
  }, []);

  const getSearchResults = () => {
    if (!search) {
      const recentPatients: Patient[] = getRecentlyViewedPatients({
        orgId: user?.tenantId!,
      });

      if (recentPatients.length) {
        return (
          <div className="flex flex-col gap-2">
            <div className="text-lg font-medium flex items-center gap-2">
              <ClockCircleOutlined /> Recently viewed patients
            </div>
            <Card
              className="max-h-full overflow-hidden shadow"
              classNames={{
                body: 'max-h-full overflow-y-auto',
              }}
              styles={{
                body: {
                  padding: 0,
                },
              }}
            >
              <div className="flex flex-col overflow-y-auto">
                {recentPatients.map((patient, index) => {
                  return (
                    <div
                      className={`hover:bg-gray-50 cursor-pointer ${
                        index !== recentPatients.length - 1
                          ? 'border-b border-gray-200'
                          : ''
                      }`}
                      onClick={() => {
                        // @ts-expect-error
                        analytics.track(
                          'Clicked Patient From Recently Viewed List',
                          {
                            email: user?.email,
                            patient_id: patient.patient_id,
                          }
                        );
                        onPatientSelect(patient.patient_id);
                      }}
                      key={patient.patient_id}
                    >
                      <PatientDetailsBar
                        firstName={patient.first_name}
                        lastName={patient.last_name}
                        email={patient.email?.email_text}
                        phone={patient.phone?.phone_text}
                        id={patient.patient_id}
                        dob={patient.birth_date}
                        ehrId={patient.ehr_id}
                        pastAppointment={patient.past_appointment_date}
                        nextAppointment={patient.next_appointment_date}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        );
      }

      return (
        <Card className="max-h-full overflow-hidden shadow">
          <div className="flex flex-col items-center justify-center">
            <div className="flex flex-col items-center justify-center gap-4 w-[480px] max-w-full">
              <img
                src="/assets/illustrations/patientSearch.svg"
                alt="patient search"
              />
              <div className="text-center text-gray-500">
                Start by searching for a patient using their name or ID. Your
                recently viewed patients will appear here.
              </div>
            </div>
          </div>
        </Card>
      );
    }
    if (isLoading) {
      return (
        <Card className="max-h-full overflow-hidden shadow">
          <Skeleton active />
        </Card>
      );
    }

    if (error || !data) {
      return (
        <div className="text-center text-gray-500">Something went wrong</div>
      );
    }

    if (!data.length) {
      return (
        <Card className="max-h-full overflow-hidden shadow">
          <Empty description="Your search did not return any results" />
        </Card>
      );
    }

    return (
      <div className="flex flex-col gap-2 overflow-hidden h-full">
        <div className="text-lg font-medium flex items-center gap-2 flex-shrink-0">
          <SearchOutlined /> {data.length} matches found
        </div>
        <Card
          className="max-h-full overflow-hidden shadow"
          classNames={{
            body: 'max-h-full overflow-y-auto',
          }}
          styles={{
            body: {
              padding: 0,
            },
          }}
        >
          <div className="flex flex-col overflow-y-auto">
            {data.map((patient, index) => {
              return (
                <div
                  className={`hover:bg-gray-50 cursor-pointer ${
                    index !== data.length - 1 ? 'border-b border-gray-200' : ''
                  }`}
                  onClick={() => {
                    // @ts-expect-error
                    analytics.track('Clicked Patient From Search Results', {
                      email: user?.email,
                      patient_id: patient.patient_id,
                    });
                    onPatientSelect(patient.patient_id);
                  }}
                  key={patient.patient_id}
                >
                  <PatientDetailsBar
                    fullName={patient.patient_Name}
                    id={patient.patient_id}
                    dob={patient.patient_birth_date}
                    pastAppointment={patient.past_appointment_date}
                    nextAppointment={patient.next_appointment_date}
                    ehrId={patient.ehr_id}
                  />
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div
      style={{ height: 'calc(100dvh - 100px)' }}
      className="page-wrapper care-connect-page overflow-hidden"
    >
      <div className="flex h-full flex-col gap-6 overflow-hidden">
        <Input
          size="large"
          placeholder="Search patients by name, ID, EHR/MRN ID or phone number"
          className="h-12 w-full flex-shrink-0"
          style={{ fontSize: '16px' }}
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          onChange={(e) => setSearch(e.target.value)}
        />
        {getSearchResults()}
      </div>
    </div>
  );
};
